// useStore.ts — Zustand store for all CV Web application state
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { subscribeWithSelector } from 'zustand/middleware';
import type { CVProject, PanelData, SiteData, MpVar, ChVar, TpVar, LbVar, ViewPreset, ShadingMode, ActiveDialog, Slot, EditMode, Elevation, AppView, CornerType, Floor, BracePlanSettings, CastLayoutSettings } from '../types/panelvar';
import type { MountType } from '../types/mountType';
import { findNextSlot, autoMark, groupPrefix, groupToDialog } from '../utils/slotUtils';
import type { SlotGroupKey } from '../utils/slotUtils';
import type { CVTemplatePayload } from '../utils/cvpFormat';
import { defaultProject, defaultPanelData, defaultProjectSettings, defaultElevations, defaultPlansData, MAX_ELEVATIONS, defaultCorners, migrateElevationCorners, wireDefaultConnections, complementCornerType, defaultBracePlanSettings, defaultCastLayoutSettings, mergePanelDefaults, mergeProjectDefaults } from '../types/panelvar';
import type { RoofPlan } from '../types/panelvar';
import { getStructuralWarnings, type StructuralWarning } from '../utils/structuralWarnings';

/** Compute next panel mark like P001, P002, ... from existing panels */
function nextPanelMark(panels: { mpvar: { panelMark: string } }[]): string {
  let max = 0;
  for (const p of panels) {
    const m = p.mpvar.panelMark.match(/^P(\d+)$/i);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `P${String(max + 1).padStart(3, '0')}`;
}

/** Deep-clone a panel, generating a new UUID, mark, and timestamps */
function deepClonePanel(src: PanelData, allPanels: PanelData[]): PanelData {
  const clone = structuredClone(src);
  clone.id = crypto.randomUUID();
  clone.mpvar.panelMark = nextPanelMark(allPanels);
  clone.createdAt = new Date().toISOString();
  clone.updatedAt = new Date().toISOString();
  return clone;
}

/** Generate section names for elevation splits */
function splitSectionNames(direction: string, count: number): string[] {
  // B/F elevations split along L↔R axis; R/L elevations split along B↔F axis
  const isBF = direction === 'B' || direction === 'F';
  const near = isBF ? 'Left' : 'Back';
  const far  = isBF ? 'Right' : 'Front';
  const dir  = direction === 'B' ? 'Back' : direction === 'F' ? 'Front' : direction === 'R' ? 'Right' : direction === 'L' ? 'Left' : direction;

  if (count === 2) return [`${dir} Elevation - ${near} End`, `${dir} Elevation - ${far} End`];
  if (count === 3) return [`${dir} Elevation - ${near} End`, `${dir} Elevation - Center`, `${dir} Elevation - ${far} End`];
  if (count === 4) return [`${dir} Elevation - ${near} End`, `${dir} Elevation - ${near} Center`, `${dir} Elevation - ${far} Center`, `${dir} Elevation - ${far} End`];
  return [`${dir} Elevation - ${near} End`, `${dir} Elevation - ${near} Center`, `${dir} Elevation - Center`, `${dir} Elevation - ${far} Center`, `${dir} Elevation - ${far} End`];
}

/** Backfill mpvar fields added by the panel-top-geometry feature.
 *  Existing panels in IndexedDB won't have topAngleL etc. — default them here. */
function migratePanelTopGeometry(panels: PanelData[]): boolean {
  let changed = false;
  for (const p of panels) {
    const m = p.mpvar as unknown as Record<string, unknown>;
    if (m.topAngleL === undefined)    { m.topAngleL = 'auto'; changed = true; }
    if (m.topAngleR === undefined)    { m.topAngleR = 'auto'; changed = true; }
    if (m.topAngleLocked === undefined) { m.topAngleLocked = true; changed = true; }
    if (m.topRadiusL === undefined)   { m.topRadiusL = 0; changed = true; }
    if (m.topRadiusR === undefined)   { m.topRadiusR = 0; changed = true; }
  }
  return changed;
}

/** Migrate legacy compass elevation names/directions to facing-relative terms.
 *  Returns true if any elevation was changed. */
function migrateCompassElevations(elevations: Elevation[]): boolean {
  const dirMap: Record<string, string> = { N: 'B', S: 'F', E: 'R', W: 'L' };
  const nameMap: Record<string, string> = { North: 'Back', South: 'Front', East: 'Right', West: 'Left' };
  let changed = false;
  for (const e of elevations) {
    if (dirMap[e.direction]) { e.direction = dirMap[e.direction]; changed = true; }
    if (nameMap[e.name])     { e.name = nameMap[e.name]; changed = true; }
  }
  return changed;
}

/** Backfill empty plans categories for projects saved before plans sub-data was added.
 *  Returns true if anything was populated. */
function migratePlansData(proj: CVProject): boolean {
  const plans = proj.plans;
  if (!plans) return false;
  // Only migrate if all non-roof categories are empty (roof was populated first)
  const hasNonRoof = plans.floorPlans.length || plans.wallSections.length ||
    plans.footingSections.length || plans.doorTypes.length || plans.windowTypes.length;
  if (hasNonRoof) return false;
  // Only backfill the Basilica sample
  if (!proj.name.toLowerCase().includes('basilica')) return false;

  plans.floorPlans = [
    { id: crypto.randomUUID(), name: 'Main Level Floor Plan', scale: 48, notes: 'Ground level — nave, transepts, apse, and narthex.' },
    { id: crypto.randomUUID(), name: 'Crypt Level Floor Plan', scale: 48, notes: 'Below-grade crypt level with memorial chapels.' },
    { id: crypto.randomUUID(), name: 'Gallery Level Floor Plan', scale: 48, notes: 'Upper gallery / triforium level overlooking the nave.' },
  ];
  plans.wallSections = [
    { id: crypto.randomUUID(), label: 'A', elevationId: proj.elevations[0]?.id, notes: 'Section through nave wall at midspan.' },
    { id: crypto.randomUUID(), label: 'B', elevationId: proj.elevations[1]?.id, notes: 'Section through south nave wall.' },
    { id: crypto.randomUUID(), label: 'C', notes: 'Section through transept wall at crossing.' },
    { id: crypto.randomUUID(), label: 'D', notes: 'Section through apse wall.' },
  ];
  plans.footingSections = [
    { id: crypto.randomUUID(), label: '1', notes: 'Continuous footing — nave walls (36″ wide × 18″ deep).' },
    { id: crypto.randomUUID(), label: '2', notes: 'Spread footing — dome piers (8′-0″ × 8′-0″ × 3′-0″ deep).' },
    { id: crypto.randomUUID(), label: '3', notes: 'Continuous footing — apse curved walls (30″ wide × 16″ deep).' },
    { id: crypto.randomUUID(), label: '4', notes: 'Grade beam — transept ends (24″ wide × 24″ deep).' },
  ];
  plans.doorTypes = [
    { id: crypto.randomUUID(), label: 'Type 1 — Main Entry', width: 120, height: 192, notes: 'Double bronze doors (10′-0″ × 16′-0″).' },
    { id: crypto.randomUUID(), label: 'Type 2 — Side Entry', width: 72, height: 120, notes: 'Single wood doors (6′-0″ × 10′-0″).' },
    { id: crypto.randomUUID(), label: 'Type 3 — Service', width: 42, height: 84, notes: 'Hollow metal doors (3′-6″ × 7′-0″).' },
  ];
  plans.windowTypes = [
    { id: crypto.randomUUID(), label: 'Type A — Nave Arched', width: 48, height: 144, notes: 'Tall arched windows (4′-0″ × 12′-0″).' },
    { id: crypto.randomUUID(), label: 'Type B — Apse Rose', width: 180, height: 180, notes: 'Large rose window (15′-0″ diameter).' },
    { id: crypto.randomUUID(), label: 'Type C — Transept', width: 60, height: 96, notes: 'Rectangular windows (5′-0″ × 8′-0″).' },
    { id: crypto.randomUUID(), label: 'Type D — West Front', width: 72, height: 120, notes: 'Triple lancet windows (6′-0″ × 10′-0″ each).' },
  ];
  return true;
}

/** Backfill empty roof-plan element arrays for the Arlington sample.
 *  Returns true if anything was populated. */
function migrateRoofElements(proj: CVProject): boolean {
  const plans = proj.plans;
  if (!plans) return false;
  const rp = plans.roofPlans?.[0];
  if (!rp) return false;

  let changed = false;

  // Phase 1: backfill empty roof plans for Arlington sample (stepped footprint)
  if (rp.elements.length === 0 && proj.name.toLowerCase().includes('arlington')) {
    rp.elements = [
      { id: crypto.randomUUID(), type: 'flat', name: 'Main Roof', x: 5236, y: 1170, width: 10472, length: 2340, ridgeHeight: 304 },
      { id: crypto.randomUUID(), type: 'flat', name: 'RTU-1 Pad', x: 2200, y: 300, width: 360, length: 240, ridgeHeight: 316 },
      { id: crypto.randomUUID(), type: 'flat', name: 'RTU-2 Pad', x: 5236, y: 300, width: 360, length: 240, ridgeHeight: 316 },
      { id: crypto.randomUUID(), type: 'flat', name: 'RTU-3 Pad', x: 8280, y: 300, width: 360, length: 240, ridgeHeight: 316 },
    ];
    changed = true;
  }

  // Phase 2: backfill ridgeHeight on any flat elements that lack it
  for (const el of rp.elements) {
    if (el.type === 'flat' && el.ridgeHeight == null) {
      el.ridgeHeight = el.name?.toLowerCase().includes('rtu') ? 316 : 304;
      changed = true;
    }
  }

  return changed;
}

import { db } from './db';

export type { ActiveDialog, ViewPreset, ShadingMode, EditMode, AppView };

export interface SnapshotRecord {
  autoId?: number;          // auto-increment PK (assigned by Dexie)
  projectId: string;
  createdAt: string;        // ISO timestamp
  data: CVProject;          // full deep copy
}

// ── UI state ─────────────────────────────────────────────
export interface PlacementContext {
  panelX: number;
  panelY: number;
  screenX: number;
  screenY: number;
}

export interface ContextMenuState {
  entityId: string | null;   // null = right-clicked empty panel
  screenX: number;
  screenY: number;
  panelX: number;
  panelY: number;
}

export interface PendingSlotTarget {
  group: string;
  index: number;
  panelX: number;
  panelY: number;
}

export interface ClipboardData {
  group: string;
  data: Record<string, unknown>;
}

export interface UIState {
  activeDialog:      ActiveDialog;
  activePanelId:     string | null;
  selectedEntities:  string[];       // entity IDs like 'rovar-0', 'panel-body'
  viewMode:          '3d' | '2d-site' | '2d';
  elevationViewMode: '2d' | '3d';
  buildingViewMode:  '2d' | '3d';
  editMode:          EditMode;
  buildingTab:       string;          // active building sidebar tab key
  shadingMode:       ShadingMode;
  viewPreset:        ViewPreset | null;
  sidebarOpen:       boolean;
  propsOpen:         boolean;
  units:             'imperial' | 'metric';
  showGrid:          boolean;
  showGround:        boolean;
  showPanelMarks:    boolean;
  loading:            boolean;
  toastMessage:       string | null;
  pendingWarnings:    StructuralWarning[] | null;
  focusField:         string | null;      // e.g. 'width', 'height', 'roofLine' — set by canvas click
  placementContext:   PlacementContext | null;
  contextMenu:        ContextMenuState | null;
  pendingSlotTarget:  PendingSlotTarget | null;
  clipboard:          ClipboardData | null;
  faceFilter:         'front' | 'back' | 'both';
  layerVisibility:    Record<MountType, boolean>;
  checkedPanelIds:    string[];
  selectedPanelIds:   string[];
  panelClipboard:     PanelData | null;
  // Multi-floor
  activeFloorId:        string | null;
  // Brace planning
  bracingViewMode:      '2d' | '3d';
  bracePlanSettings:    BracePlanSettings;
  checkedBracePanelIds: string[];
  bracePanelOverrides:  Record<string, Partial<BracePlanSettings>>;
  // Cast layout planning
  castViewMode:         '2d' | '3d';
  castLayoutSettings:   CastLayoutSettings;
}

// ── Full store ────────────────────────────────────────────────────────────────
export interface CVStore {
  // Project
  project:        CVProject | null;
  projectList:    { id: string; name: string; updatedAt: string }[];

  // UI
  ui:             UIState;

  // Project actions
  newProject:     (name?: string, jobNumber?: string, client?: string, engineer?: string, address?: string) => void;
  loadProject:    (id: string) => Promise<void>;
  saveProject:    () => Promise<void>;  closeProject:   () => void;  importProject:    (json: string) => Promise<void>;
  exportProject:    () => void;
  loadSampleProject: (sampleKey?: string) => Promise<void>;
  importCVProject:  (project: CVProject, mode: 'replace' | 'new') => Promise<void>;
  importCVPanel:    (panel: PanelData) => void;
  importCVSite:     (site: SiteData) => void;
  importCVTemplate: (template: CVTemplatePayload) => void;
  refreshProjectList: () => Promise<void>;
  deleteProject:  (id: string) => Promise<void>;
  updateProject:  (patch: Partial<CVProject>) => void;
  updateSite:     (patch: Partial<SiteData>) => void;

  // Edit mode
  setEditMode:    (mode: EditMode) => void;

  // Elevation actions
  addElevation:              (name: string, direction: string) => void;
  removeElevation:           (id: string) => void;
  renameElevation:           (id: string, name: string) => void;
  assignPanelToElevation:    (elevationId: string, panelId: string) => void;
  unassignPanelFromElevation:(elevationId: string, panelId: string) => void;
  reorderElevation:          (fromIndex: number, toIndex: number) => void;
  reorderPanelInElevation:   (elevationId: string, fromIndex: number, toIndex: number) => void;
  splitElevation:            (id: string, sections: number, panelsPerSection: number[]) => void;
  joinElevations:            (ids: string[]) => void;
  sortElevation:             (id: string, order: 'asc' | 'desc') => void;
  setElevationCorner:        (id: string, side: 'left' | 'right', type: CornerType) => void;
  setElevationConnection:    (id: string, side: 'left' | 'right', targetId: string | undefined) => void;
  movePanelBetweenElevations:(fromElevId: string, panelIdx: number, toElevId: string, toIdx: number) => void;
  clonePanelAtPosition:      (panelId: string, elevationId: string, position: 'before' | 'after') => void;

  // Snapshot actions
  createSnapshot:            () => Promise<void>;
  restoreSnapshot:           (autoId: number) => Promise<void>;
  deleteSnapshot:            (autoId: number) => Promise<void>;
  getSnapshots:              () => Promise<SnapshotRecord[]>;

  // Panel actions
  addPanel:       () => void;
  deletePanel:    (id: string) => void;
  duplicatePanel: (id: string) => void;
  setActivePanel:   (id: string) => void;
  setActivePanelId: (id: string) => void;
  updatePanel:    (panelId: string, patch: Partial<PanelData>) => void;
  updateMpVar:    (panelId: string, patch: Partial<MpVar>) => void;
  updateChVar:    (panelId: string, patch: Partial<ChVar>) => void;
  updateTpVar:    (panelId: string, patch: Partial<TpVar>) => void;
  updateLbVar:    (panelId: string, patch: Partial<LbVar>) => void;

  // Entity selection actions
  selectEntity:            (id: string) => void;
  toggleSelectEntity:      (id: string) => void;
  setSelection:            (ids: string[]) => void;
  addToSelection:          (ids: string[]) => void;
  clearSelection:          () => void;
  deleteSelectedEntities:  () => void;

  // Panel selection actions
  selectPanel:             (id: string) => void;
  toggleSelectPanel:       (id: string) => void;
  setSelectedPanels:       (ids: string[]) => void;
  clearPanelSelection:     () => void;
  copySelectedPanels:      () => void;
  pastePanelsToElevation:  (elevationId: string, afterPanelId?: string) => void;
  deleteSelectedPanels:    () => void;

  // UI actions
  openDialog:      (d: ActiveDialog) => void;
  setActiveDialog: (d: ActiveDialog) => void;
  closeDialog:     () => void;
  setViewMode:     (m: 'viewMode', v: UIState['viewMode']) => void;
  setElevationViewMode: (v: '2d' | '3d') => void;
  setBuildingViewMode:   (v: '2d' | '3d') => void;
  setShadingMode:  (v: ShadingMode) => void;
  setViewPreset:   (v: ViewPreset | null) => void;
  clearViewPreset: () => void;
  toggleSidebar:   () => void;
  toggleProps:     () => void;
  toggleUnits:     () => void;
  toggleGrid:             () => void;
  toggleGround:           () => void;
  togglePanelMarks:       () => void;
  showToast:              (msg: string, durationMs?: number) => void;
  clearPendingWarnings:   () => void;
  showWarnings:           (panelId: string) => void;
  setFocusField:          (field: string | null) => void;

  // Placement & context menu actions
  startPlacement:         (panelX: number, panelY: number, screenX: number, screenY: number) => void;
  commitPlacement:        (group: SlotGroupKey) => void;
  cancelPlacement:        () => void;
  showContextMenu:        (entityId: string | null, screenX: number, screenY: number, panelX: number, panelY: number) => void;
  closeContextMenu:       () => void;
  copyEntity:             (entityId: string) => void;
  pasteEntity:            (panelX: number, panelY: number) => void;
  editEntity:             (entityId: string) => void;
  deleteEntity:           (entityId: string) => void;
  clearPendingSlotTarget: () => void;
  setFaceFilter:          (filter: 'front' | 'back' | 'both') => void;
  toggleLayerVisibility:  (mt: MountType) => void;
  setLayerVisibility:     (mt: MountType, visible: boolean) => void;
  toggleCheckedPanel:     (id: string) => void;
  checkAllPanels:         () => void;
  uncheckAllPanels:       () => void;

  // Multi-floor actions
  setActiveFloorId:       (id: string | null) => void;
  cloneFloor:             (sourceFloorId: string) => void;
  removeFloor:            (id: string) => void;

  // Brace plan actions
  setBracingViewMode:     (v: '2d' | '3d') => void;
  setBracePlanSettings:   (patch: Partial<BracePlanSettings>) => void;
  toggleBracePanel:       (id: string) => void;
  setBraceAllPanels:      (ids: string[], _checked?: boolean) => void;
  setBracePanelOverride:  (id: string, patch: Partial<BracePlanSettings>) => void;
  clearBracePanelOverride:(id: string) => void;

  // Cast layout actions
  setCastViewMode:        (v: '2d' | '3d') => void;
  setCastLayoutSettings:  (patch: Partial<CastLayoutSettings>) => void;
}

const DEFAULT_UI: UIState = {
  activeDialog:     'none',
  activePanelId:    null,
  selectedEntities: [],
  viewMode:         '3d',
  elevationViewMode: '2d',
  buildingViewMode: '2d',
  editMode:         'panel',
  buildingTab:      'overall',
  shadingMode:      'shaded',
  viewPreset:       'fl-iso',
  sidebarOpen:      true,
  propsOpen:        true,
  units:            'imperial',
  showGrid:          true,
  showGround:        false,
  showPanelMarks:    true,
  loading:           false,
  toastMessage:      null,
  pendingWarnings:   null,
  focusField:        null,
  placementContext:  null,
  contextMenu:       null,
  pendingSlotTarget: null,
  clipboard:         null,
  faceFilter:        'both',
  layerVisibility:   { void: true, recess: true, proud: true, embed: true },
  checkedPanelIds:   [],
  selectedPanelIds:  [],
  panelClipboard:    null,
  activeFloorId:        null,
  bracingViewMode:      '3d',
  bracePlanSettings:    defaultBracePlanSettings(),
  checkedBracePanelIds: [],
  bracePanelOverrides:  {},
  castViewMode:         '2d',
  castLayoutSettings:   defaultCastLayoutSettings(),
};

function patchPanel(panels: PanelData[], id: string, patch: Partial<PanelData>): PanelData[] {
  return panels.map(p => p.id === id ? { ...p, ...patch, updatedAt: new Date().toISOString() } : p);
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;

/** Compute composite AppView from current UI state */
function computeAppView(ui: UIState): AppView {
  if (ui.editMode === 'project') return 'project';
  if (ui.editMode === 'building') return 'building';
  if (ui.editMode === 'elevations') return ui.elevationViewMode === '3d' ? 'elevations-3d' : 'elevations-2d';
  return ui.viewMode === '2d' ? 'panel-2d' : 'panel-3d';
}

export const useStore = create<CVStore>()(
  subscribeWithSelector((set, get) => ({
    project:     null,
    projectList: [],
    ui:          DEFAULT_UI,

    // ── Project ───────────────────────────────────────────────────────────────
    newProject: (name?, jobNumber?, client?, engineer?, address?) => {
      const proj = defaultProject();
      if (name)       proj.name       = name;
      if (jobNumber)  proj.jobNumber  = jobNumber;
      if (client)     proj.client     = client;
      if (engineer)   proj.engineer   = engineer;
      if (address)    proj.address    = address;
      // Immediately create first panel
      const panel = defaultPanelData(crypto.randomUUID());
      panel.mpvar.panelMark = 'P001';
      proj.panels = [panel];
      set({ project: proj, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: panel.id } });
      db.projects.put(proj).catch(err => {
        if (import.meta.env.DEV) console.error('[CV] newProject persist error:', err);
      });
    },

    loadProject: async (id) => {
      set(s => ({ ui: { ...s.ui, loading: true } }));
      try {
        const proj = await db.projects.get(id);
        if (proj) {
          // Backfill settings for projects saved before TCA/ACI defaults
          if (!proj.settings || typeof proj.settings.concreteStrength === 'undefined') {
            proj.settings = defaultProjectSettings();
          }
          // Backfill elevations for projects saved before elevation model
          if (!proj.elevations) {
            proj.elevations = defaultElevations();
          }
          // Migrate compass directions → facing-relative terms
          if (migrateCompassElevations(proj.elevations)) {
            db.projects.put(proj).catch(() => {});
          }
          // Migrate corner types for old elevations
          if (proj.elevations.some(e => migrateElevationCorners(e))) {
            db.projects.put(proj).catch(() => {});
          }
          // Wire default connections if none exist
          if (wireDefaultConnections(proj.elevations)) {
            db.projects.put(proj).catch(() => {});
          }
          // Backfill panel top-geometry fields for panels created before that feature
          if (migratePanelTopGeometry(proj.panels)) {
            db.projects.put(proj).catch(() => {});
          }
          // Backfill plans for projects saved before plans model
          if (!proj.plans) {
            proj.plans = null;  // Will initialize on first use
          }
          // Backfill empty plans categories for Basilica sample
          if (migratePlansData(proj)) {
            db.projects.put(proj).catch(() => {});
          }
          // Backfill empty roof elements for Arlington sample
          if (migrateRoofElements(proj)) {
            db.projects.put(proj).catch(() => {});
          }
          // Replace stale Arlington sample with current geometry
          if (proj.name.toLowerCase().includes('arlington') && (proj._sampleDataVersion ?? 0) < 6) {
            await db.projects.delete(id);
            set(s => ({ ui: { ...s.ui, loading: false } }));
            await get().loadSampleProject();
            get().showToast('Arlington sample updated — footprint shape fixed');
            return;
          }
          // Always open on the project home view (weather + map + dashboard);
          // user can navigate into building/elevations/panel from there.
          set({ project: proj, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: proj.panels[0]?.id ?? null } });
        } else {
          get().showToast('Project not found');
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] loadProject error:', err);
        get().showToast('Failed to load project — storage may be unavailable');
      } finally {
        set(s => ({ ui: { ...s.ui, loading: false } }));
      }
    },

    saveProject: async () => {
      const { project } = get();
      if (!project) return;
      const updated = { ...project, updatedAt: new Date().toISOString() };
      try {
        await db.projects.put(updated);
        set({ project: updated });
        get().showToast('Project saved');
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] saveProject error:', err);
        get().showToast('Save failed — storage may be full or unavailable');
      }
    },

    closeProject: () => {
      set({ project: null, ui: DEFAULT_UI });
    },

    importProject: async (json) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        get().showToast('Import failed — file is not valid JSON');
        return;
      }
      // Minimal shape validation
      if (
        typeof parsed !== 'object' || parsed === null ||
        !('name' in parsed) ||
        !('panels' in parsed) || !Array.isArray((parsed as { panels: unknown }).panels)
      ) {
        get().showToast('Import failed — file does not look like a CV project');
        return;
      }
      const proj = parsed as CVProject;
      proj.id = crypto.randomUUID(); // new ID to avoid collision
      if (!proj.elevations) proj.elevations = defaultElevations();
      migrateCompassElevations(proj.elevations);
      proj.elevations.forEach(e => migrateElevationCorners(e));
      wireDefaultConnections(proj.elevations);
      migratePanelTopGeometry(proj.panels);
      try {
        await db.projects.put(proj);
        set({ project: proj, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: proj.panels[0]?.id ?? null } });
        get().showToast('Project imported');
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] importProject error:', err);
        get().showToast('Import failed — could not save to storage');
      }
    },

    exportProject: () => {
      const { project } = get();
      if (!project) return;
      const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${project.name.replace(/\s+/g, '_')}.cvproj.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    loadSampleProject: async (_sampleKey?: string) => {
      // ── Arlington Airport Terminal Services Building ─────────────────────────
      // Stepped north face: 873' E-W × 165' N-S (with 15' service alcoves at ends)
      // 28'-0" panels (336"), 26'-0" plate (312")
      // 83 panels total: 28 Front + 30 Back (5 sub-elevations) + 12 Right + 13 Left
      // Exercises all 21 PanelData feature groups across 3 panel types.
      const now = new Date().toISOString();
      const proj = defaultProject();
      proj.name = 'Arlington Airport Terminal Services Bldg';
      proj.buildingName = 'Arlington Municipal Airport — Terminal Services Building';
      proj.jobNumber = 'ARL-2026-001';
      proj.client = 'Port of Arlington';
      proj.engineer = 'Northwest Structural Group';
      proj.superintendent = 'T. Hansen';
      proj.address = '17212 51st Ave NE';
      proj.city = 'Arlington';
      proj.state = 'WA';

      // ── Panels — 3 types covering all 21 PanelData feature groups ──────────────
      // Stepped north face: BLE/BRE are northernmost; center BC recesses 15' south.
      // 85 panels: 28 Front + (2+1+24+1+2) Back + 14 Right + 13 Left
      //
      // Closure math (raw panel widths):
      //   X: Back=(2+24+2)×374−2T = 10,457.5  |  Front: 28×W_F = 10,457.5 ✓
      //   Y: BLC↑+BRC↓ cancel; 14×167 ≈ 2,349  |  Left: 13×180 ≈ 2,351 ✓

      const H     = 336;    // 28'-0" panel height (inches)
      const ELEV  = 312;    // 26'-0" plate line (inches)
      const THICK = 7.25;   // 7¼" standard thickness
      const W_FB  = 374;    // Back panel width: 31'-2"
      const W_JOG = 180;    // Jog (perpendicular) panel width: 15'-0" (service bay depth)
      const W_R   = 167;    // Right end panel width: 13'-11" (14 × 167" = 2,338")
      const W_L   = 180;    // Left end panel width:  15'-0" (13 × 180" = 2,340" = 195')
      // Front panel width: back east-going span minus 2T corner overlap, divided by 28
      // Back span = (2+24+2)×374 = 10,472; minus 2×7.25 = 10,457.5; / 28 ≈ 373.48"
      const W_F   = ((2 + 24 + 2) * W_FB - 2 * THICK) / 28;
      const jobNum   = proj.jobNumber;
      const projName = proj.name;
      const dateStr  = now.slice(0, 10);

      // ── Helper: base panel with standard mpvar + chamfer ──────────────────────
      const makeBase = (mark: string, w: number): PanelData => {
        const p = defaultPanelData(crypto.randomUUID());
        p.mpvar.panelMark   = mark;
        p.mpvar.projectName = projName;
        p.mpvar.jobNumber   = jobNum;
        p.mpvar.width       = w;
        p.mpvar.height      = H;
        p.mpvar.thickness   = THICK;
        p.mpvar.topElev     = ELEV / 12;
        p.mpvar.bottomElev  = 0;
        p.mpvar.botElev     = 0;
        p.mpvar.roofLine    = ELEV * 0.975;
        p.mpvar.slabThickness = 5;
        p.mpvar.revision    = 'A';
        p.mpvar.date        = dateStr;
        p.mpvar.drawBy      = 'CW';
        p.mpvar.checkBy     = 'TH';
        p.mpvar.finish      = 'Smooth form';
        p.mpvar.quantity    = 1;
        p.chvar = { top: 0.75, bottom: 0.75, left: 0.75, right: 0.75, reveal: 0 };
        return p;
      };

      // ── Helper: pick points + brace points at ¼ and ¾ width ─────────────────
      const addHardware = (p: PanelData, w: number) => {
        const x1 = Math.round(w * 0.25);
        const x2 = Math.round(w * 0.75);
        p.ppvar[0] = { enabled: true, data: { mark: 'PP1', x: x1, y: 198, embedLen: 12, capacity: 10, size: 1.25, load: 0 } };
        p.ppvar[1] = { enabled: true, data: { mark: 'PP2', x: x2, y: 198, embedLen: 12, capacity: 10, size: 1.25, load: 0 } };
        p.bpvar[0] = { enabled: true, data: { mark: 'BP1', x: x1, y: 220, type: 'strongback', embedLen: 12, size: 1.25 } };
        p.bpvar[1] = { enabled: true, data: { mark: 'BP2', x: x2, y: 220, type: 'strongback', embedLen: 12, size: 1.25 } };
      };

      // ── Helper: panel-to-panel weld connections ───────────────────────────────
      const addWeldConnections = (p: PanelData, sides: 'both' | 'left' | 'right' = 'both') => {
        const wc = (mark: string, sL: boolean, sR: boolean, y: number, idx: number) => {
          p.wcvar[idx] = {
            enabled: true,
            data: {
              mark, sideL: sL, sideR: sR, x: 0, y,
              wcType: '4A', structId: 'S3.1', fabId: '4A',
              type: 'plate', face: 'back',
              plateW: 6, plateH: 8, anchors: 4, notes: '',
              width: 6, height: 8, thick: 0.375, studs: 4, studDia: 0.75,
            },
          };
        };
        if (sides === 'both' || sides === 'left') {
          wc('WC-L1', true, false, 96,  0);
          wc('WC-L2', true, false, 240, 1);
        }
        if (sides === 'both' || sides === 'right') {
          wc('WC-R1', false, true, 96,  sides === 'both' ? 2 : 0);
          wc('WC-R2', false, true, 240, sides === 'both' ? 3 : 1);
        }
      };

      // ── Front panels ARL-F01–F28 (South Office face, ~373.48" × 336" × 7.25") ──
      // 28 × W_F = back east-going span − 2T = 10,457.5"
      // Covers: mpvar, wdvar, drvar, fhvar, fvvar, plvar, llvar, tpvar, chvar, ppvar, bpvar, wcvar
      const frontIds: string[] = [];
      for (let i = 1; i <= 28; i++) {
        const p = makeBase(`ARL-F${String(i).padStart(2, '0')}`, W_F);

        // Two 4'×6' fixed windows at ¼ and ¾ width, 10' sill
        p.wdvar[0] = { enabled: true, data: { mark: 'W1', type: 'fixed', x: 94,  y: 156, width: 48, height: 72, sillHeight: 120, sill: 120, headHeight: 192, jamb: 2 } };
        p.wdvar[1] = { enabled: true, data: { mark: 'W2', type: 'fixed', x: 280, y: 156, width: 48, height: 72, sillHeight: 120, sill: 120, headHeight: 192, jamb: 2 } };

        // Man door + lintel on panels F05, F14, F23 (3 entry points along front)
        if (i === 5 || i === 14 || i === 23) {
          p.drvar[0] = { enabled: true, data: { mark: 'D1', type: 'double', x: 187, y: 60, width: 72, height: 120, threshold: 0, jamb: 2 } };
          p.llvar[0] = { enabled: true, data: { mark: 'LL1', overOpening: 'D1', depth: 8, height: 12, projection: 3 } };
        }

        // Horizontal reveals at 10' and 20'
        p.fhvar[0] = { enabled: true, data: { mark: 'FH1', type: 'reveal',      y: 120, width: W_F, height: 2, depth: 0.75, xStart: 0, xEnd: W_F } };
        p.fhvar[1] = { enabled: true, data: { mark: 'FH2', type: 'rustication', y: 240, width: W_F, height: 4, depth: 1.0,  xStart: 0, xEnd: W_F } };

        // Vertical reveals at ¼ and ¾ width
        p.fvvar[0] = { enabled: true, data: { mark: 'FV1', type: 'reveal', x: 94,  width: 2, depth: 0.75, yStart: 0, yEnd: H } };
        p.fvvar[1] = { enabled: true, data: { mark: 'FV2', type: 'reveal', x: 280, width: 2, depth: 0.75, yStart: 0, yEnd: H } };

        // Steel top plate on all front panels
        p.tpvar = { enabled: true, thickness: 1.5, width: 6, material: 'steel', mark: 'TP-1' };

        // Left corner pilaster on F01 only
        if (i === 1) {
          p.plvar[0] = { enabled: true, data: { mark: 'PL1', side: 'left', width: 18, projection: 4, height: H } };
        }

        addHardware(p, W_F);
        addWeldConnections(p);
        proj.panels.push(p);
        frontIds.push(p.id);
      }

      // ── Side panel builder (Right + Left end walls + jog panels) ──────────────
      // Covers: mpvar, wdvar, fhvar, fvvar, tsvar(first), fsvar(first), chvar, ppvar, bpvar, wcvar
      const makeSidePanel = (mark: string, w: number, localIdx: number): PanelData => {
        const p  = makeBase(mark, w);
        const cx = Math.round(w / 2);

        // One centered 4'×6' window at 10' sill
        p.wdvar[0] = { enabled: true, data: { mark: 'W1', type: 'fixed', x: cx, y: 156, width: 48, height: 72, sillHeight: 120, sill: 120, headHeight: 192, jamb: 2 } };

        // Horizontal reveal at 20'
        p.fhvar[0] = { enabled: true, data: { mark: 'FH1', type: 'reveal', y: 240, width: w, height: 2, depth: 0.75, xStart: 0, xEnd: w } };

        // Vertical reveal at 60% of center
        p.fvvar[0] = { enabled: true, data: { mark: 'FV1', type: 'reveal', x: Math.round(cx * 0.6), width: 2, depth: 0.75, yStart: 0, yEnd: H } };

        // First panel in each end wall run gets top steps + footing steps
        if (localIdx === 1) {
          p.tsvar[0] = { enabled: true, data: { mark: 'TS1', x: cx - 24, width: 48, height: 12, depth: 3 } };
          p.fsvar[0] = { enabled: true, data: { mark: 'FS1', x: cx - 60, width: 120, height: 6 } };
        }

        addHardware(p, w);
        addWeldConnections(p);
        return p;
      };

      // ── Back panels — 5 sub-elevations (stepped north face) ─────────────────
      // Left End (2×374") → Left Center jog (1×180" south) → Center (24×374")
      //   → Right Center jog (1×180" south) → Right End (2×374")
      // Covers: mpvar, rovar, dlvar, sbvar, rbvar, fhvar, lbvar, ssvar, sdvar, chvar, ppvar, bpvar, wcvar

      const makeBackPanel = (mark: string, w: number, idx: number): PanelData => {
        const p = makeBase(mark, w);
        p.rovar[0] = { enabled: true, data: { mark: 'RO1', x: Math.round(w / 2), y: 80, width: 168, height: 160 } };
        if (idx % 2 === 0) {
          p.dlvar[0] = { enabled: true, data: { mark: 'DL1', x: Math.round(w / 2), y: 45, width: 144, height: 90, depth: 18 } };
        }
        p.fhvar[0] = { enabled: true, data: { mark: 'FH1', type: 'reveal', y: 240, width: w, height: 2, depth: 0.75, xStart: 0, xEnd: w } };
        p.lbvar = { enabled: true, y: 300, depth: 5, width: 8, mark: 'LB-1' };
        if (idx === 1) {
          p.sbvar[0] = { enabled: true, data: { mark: 'SB1', x: 32,  y: 264, width: 12, height: 6,  depth: 4 } };
          p.rbvar[0] = { enabled: true, data: { mark: 'RB1', x: Math.round(w - 32), y: 264, diameter: 4, depth: 4 } };
          p.ssvar[0] = { enabled: true, data: { mark: 'SS1', side: 'left', x: 48, width: 36, height: 12, depth: 8, elevation: 120 } };
          p.sdvar[0] = { enabled: true, data: { mark: 'SD1', x: 80,  y: 0, size: '#5', spacing: 12, embedLen: 12, projLen: 6 } };
          p.sdvar[1] = { enabled: true, data: { mark: 'SD2', x: Math.round(w - 94), y: 0, size: '#5', spacing: 12, embedLen: 12, projLen: 6 } };
        }
        addHardware(p, w);
        addWeldConnections(p);
        return p;
      };

      // Left End — 2 panels × 374" (east)
      const backLeftEndIds: string[] = [];
      for (let i = 1; i <= 2; i++) {
        const p = makeBackPanel(`ARL-B${String(i).padStart(2, '0')}`, W_FB, i);
        proj.panels.push(p);
        backLeftEndIds.push(p.id);
      }

      // Left Center jog — 1 panel × 180" (perpendicular, south)
      const jogLeft = makeSidePanel('ARL-BJ1', W_JOG, 0);
      proj.panels.push(jogLeft);

      // Center — 24 panels × 374" (east)
      const backCenterIds: string[] = [];
      for (let i = 3; i <= 26; i++) {
        const p = makeBackPanel(`ARL-B${String(i).padStart(2, '0')}`, W_FB, i);
        proj.panels.push(p);
        backCenterIds.push(p.id);
      }

      // Right Center jog — 1 panel × 180" (perpendicular, south)
      const jogRight = makeSidePanel('ARL-BJ2', W_JOG, 0);
      proj.panels.push(jogRight);

      // Right End — 2 panels × 374" (east)
      const backRightEndIds: string[] = [];
      for (let i = 27; i <= 28; i++) {
        const p = makeBackPanel(`ARL-B${String(i).padStart(2, '0')}`, W_FB, i);
        proj.panels.push(p);
        backRightEndIds.push(p.id);
      }

      const rightIds: string[] = [];
      for (let i = 1; i <= 14; i++) {
        const p = makeSidePanel(`ARL-S${String(i).padStart(2, '0')}`, W_R, i);
        proj.panels.push(p);
        rightIds.push(p.id);
      }

      const leftIds: string[] = [];
      for (let i = 15; i <= 27; i++) {
        const p = makeSidePanel(`ARL-S${String(i).padStart(2, '0')}`, W_L, i - 14);
        proj.panels.push(p);
        leftIds.push(p.id);
      }

      // ── Elevations — 8-elevation chain with recessed center bay ──────────────
      // Chain: BLE(east) → [CW] BLC(south) → [CCW] BC(east) → [CCW] BRC(north)
      //   → [CW] BRE(east) → [CW] R(south) → [CW] F(west) → [CW] L(north) → [CW] BLE
      const bleId = crypto.randomUUID();
      const blcId = crypto.randomUUID();
      const bcId  = crypto.randomUUID();
      const brcId = crypto.randomUUID();
      const breId = crypto.randomUUID();
      const rId   = crypto.randomUUID();
      const fId   = crypto.randomUUID();
      const lId   = crypto.randomUUID();

      const elevations: Elevation[] = [
        // Back Left End — 2 panels going east
        // L→BLE corner: L terminates, BLE is continuous
        // BLE→BLC corner: BLE is continuous, BLC terminates
        { id: bleId, name: 'Back Elevation - Left End', direction: 'B', panelIds: backLeftEndIds, sortOrder: 0,
          leftCorner: 'continuous', rightCorner: 'continuous',
          leftConnectsTo: lId, rightConnectsTo: blcId },
        // Back Left Center jog — 1 panel going south (CW from east → recess)
        // BLE→BLC corner: BLC terminates into BLE interior face (south side)
        // BLC→BC corner: BLC terminates into BC exterior face (north side)
        { id: blcId, name: 'Back Elevation - Left Center', direction: 'B', panelIds: [jogLeft.id], sortOrder: 1,
          leftCorner: 'butt-int', rightCorner: 'butt-ext',
          leftConnectsTo: bleId, rightConnectsTo: bcId },
        // Back Center — 24 panels going east
        // BLC→BC corner: BC is continuous, BLC terminates
        // BC→BRC corner: BC is continuous, BRC terminates
        { id: bcId, name: 'Back Elevation - Center', direction: 'B', panelIds: backCenterIds, sortOrder: 2,
          leftCorner: 'continuous', rightCorner: 'continuous',
          leftConnectsTo: blcId, rightConnectsTo: brcId },
        // Back Right Center jog — 1 panel going north (CCW from east → return to BRE)
        // BC→BRC corner: BRC terminates into BC exterior face (north side)
        // BRC→BRE corner: BRC terminates into BRE interior face (south side)
        { id: brcId, name: 'Back Elevation - Right Center', direction: 'B', panelIds: [jogRight.id], sortOrder: 3,
          leftCorner: 'butt-ext', rightCorner: 'butt-int',
          leftConnectsTo: bcId, rightConnectsTo: breId },
        // Back Right End — 2 panels going east
        // BRC→BRE corner: BRE is continuous, BRC terminates
        // BRE→R corner: BRE is continuous, R terminates
        { id: breId, name: 'Back Elevation - Right End', direction: 'B', panelIds: backRightEndIds, sortOrder: 4,
          leftCorner: 'continuous', rightCorner: 'continuous',
          leftConnectsTo: brcId, rightConnectsTo: rId },
        // Right (East End) — 12 panels going south
        { id: rId, name: 'Right (East End)', direction: 'R', panelIds: rightIds, sortOrder: 5,
          leftCorner: 'butt-int', rightCorner: 'butt-int',
          leftConnectsTo: breId, rightConnectsTo: fId },
        // Front (South Office) — 28 panels going west
        { id: fId, name: 'Front (South Office)', direction: 'F', panelIds: frontIds, sortOrder: 6,
          leftCorner: 'continuous', rightCorner: 'continuous',
          leftConnectsTo: rId, rightConnectsTo: lId },
        // Left (West End) — 13 panels going north
        { id: lId, name: 'Left (West End)', direction: 'L', panelIds: leftIds, sortOrder: 7,
          leftCorner: 'butt-int', rightCorner: 'butt-int',
          leftConnectsTo: fId, rightConnectsTo: bleId },
      ];
      proj.elevations = elevations;

      // ── Plans ──────────────────────────────────────────────────────────────────
      const plans = defaultPlansData();

      plans.overallPlan = {
        id: crypto.randomUUID(),
        name: 'Overall Site Plan',
        scale: 96,
        notes: "Arlington Airport Terminal Services Building — 873'×195' tilt-up with recessed center bay.",
      };

      plans.floorPlans = [
        { id: crypto.randomUUID(), name: 'Main Floor Plan', scale: 48, notes: 'Single-story — terminal ops, offices, and service bays.' },
      ];

      // Footprint: 10,472" E-W × 2,340" N-S (center 748' protrudes 15' north)
      // Roof covers main rectangular bounding box; alcoves below main roof level
      // Center footprint: X≈5236" (10472/2), Y≈1170" (2340/2)
      const roofCX = 5236;
      const roofCY = 1170;
      const flatRoof: RoofPlan = {
        id: crypto.randomUUID(),
        name: 'Roof Plan',
        elements: [
          // Main roof area — built-up flat roof covering full stepped footprint
          { id: crypto.randomUUID(), type: 'flat', name: 'Main Roof', x: roofCX, y: roofCY, width: 10472, length: 2340, ridgeHeight: 304 },
          // Mechanical equipment pads on north service center (3 RTU pads — curb height above deck)
          { id: crypto.randomUUID(), type: 'flat', name: 'RTU-1 Pad', x: 2200, y: 300, width: 360, length: 240, ridgeHeight: 316 },
          { id: crypto.randomUUID(), type: 'flat', name: 'RTU-2 Pad', x: roofCX, y: 300, width: 360, length: 240, ridgeHeight: 316 },
          { id: crypto.randomUUID(), type: 'flat', name: 'RTU-3 Pad', x: 8280, y: 300, width: 360, length: 240, ridgeHeight: 316 },
        ],
        notes: "Built-up flat roof — 873'×195' stepped footprint. Drains at column lines. Mech equipment at north service side.",
      };
      plans.roofPlans = [flatRoof];

      plans.wallSections = [
        { id: crypto.randomUUID(), label: 'A', elevationId: elevations[2].id, notes: 'Typical south office panel — wall section at panel edge.' },
        { id: crypto.randomUUID(), label: 'B', elevationId: elevations[0].id, notes: 'Typical north service panel — dock leveler pit and ledger detail.' },
        { id: crypto.randomUUID(), label: 'C', elevationId: elevations[1].id, notes: 'East end panel — corner condition at south office elevation.' },
        { id: crypto.randomUUID(), label: 'D', notes: 'Foundation section — continuous footing at dock leveler panels.' },
      ];

      plans.footingSections = [
        { id: crypto.randomUUID(), label: '1', notes: "Typical continuous footing — 36\u2033 wide \u00D7 18\u2033 deep, #5 @ 12\u2033 o.c." },
        { id: crypto.randomUUID(), label: '2', notes: "Dock leveler pit — reinforced pit walls, 18\u2033 deep \u00D7 width per DL schedule." },
      ];

      plans.doorTypes = [
        { id: crypto.randomUUID(), label: 'Type A — Man Door',      width: 72,  height: 120, notes: "Double HM door — 6'-0\u2033 \u00D7 10'-0\u2033. Office entry panels F05, F14, F23." },
        { id: crypto.randomUUID(), label: 'Type B — Overhead Door', width: 168, height: 160, notes: "14'-0\u2033 \u00D7 13'-4\u2033 sectional overhead. All service bays." },
      ];

      plans.windowTypes = [
        { id: crypto.randomUUID(), label: 'Type 1 — Fixed Office', width: 48, height: 72, notes: "4'-0\u2033 \u00D7 6'-0\u2033 fixed aluminum storefront. Office and end wall panels." },
      ];

      proj.plans = plans;

      // ── Settings ───────────────────────────────────────────────────────────────
      proj.settings.concreteStrength = 4000;
      proj.settings.concreteDensity  = 150;
      proj.settings.rebarSize        = '#5';
      proj.settings.rebarSpacing     = 18;
      proj.settings.rebarLayers      = 'single';
      proj.settings.clearCover       = 0.75;
      proj.settings.jointWidth       = 0.375;   // 3/8" expansion joint per side

      proj._sampleDataVersion = 6;
      proj.createdAt = now;
      proj.updatedAt = now;

      // ── Persist and open ────────────────────────────────────────────────────────
      try {
        await db.projects.put(proj);
        set({
          project: proj,
          ui: {
            ...DEFAULT_UI,
            editMode: 'building',
            activePanelId: proj.panels[0]?.id ?? null,
          },
        });
        get().showToast(`Arlington Airport sample loaded — ${proj.panels.length} panels`);
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] loadSampleProject error:', err);
        get().showToast('Failed to load sample project');
      }
    },

    importCVProject: async (project, mode) => {
      const now = new Date().toISOString();
      const hydrated = mergeProjectDefaults(project);
      const proj = mode === 'new'
        ? { ...hydrated, id: crypto.randomUUID(), createdAt: now, updatedAt: now }
        : { ...hydrated };
      // CV-806 — hydrate any *var groups the importer omitted before
      // downstream renderers iterate them.
      proj.panels = proj.panels.map(mergePanelDefaults);
      // Backfill elevations if missing
      if (!proj.elevations) proj.elevations = defaultElevations();
      migrateCompassElevations(proj.elevations);
      proj.elevations.forEach(e => migrateElevationCorners(e));
      wireDefaultConnections(proj.elevations);
      migratePanelTopGeometry(proj.panels);
      try {
        await db.projects.put(proj);
        set({ project: proj, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: proj.panels[0]?.id ?? null } });
        get().showToast(mode === 'replace' ? 'Project replaced' : 'Project imported');
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] importCVProject error:', err);
        get().showToast('Import failed — could not save to storage');
      }
    },

    importCVPanel: (panel) => {
      const now = new Date().toISOString();
      // CV-806 — hydrate omitted *var groups so renderers don't crash on
      // .forEach of undefined.
      const newPanel = mergePanelDefaults({ ...panel, id: crypto.randomUUID(), createdAt: now, updatedAt: now });
      migratePanelTopGeometry([newPanel]);
      // WelcomeScreen path: no project open → spin up a default one so the
      // panel has a home. From within an open project, just append.
      const current = get().project;
      const base = current ?? { ...defaultProject(), panels: [] };
      const updated = { ...base, panels: [...base.panels, newPanel], updatedAt: now };
      set({ project: updated, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: newPanel.id } });
      db.projects.put(updated);
      get().showToast(current
        ? `Panel ${newPanel.mpvar.panelMark || 'imported'} added`
        : `New project created with panel ${newPanel.mpvar.panelMark || 'imported'}`);
    },

    importCVSite: (site) => {
      const now = new Date().toISOString();
      // WelcomeScreen path: no project open → spin up a default one to hold
      // the site. From within an open project, just attach/replace.
      const current = get().project;
      const base = current ?? defaultProject();
      const updated = { ...base, site, updatedAt: now };
      set({ project: updated, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: updated.panels[0]?.id ?? null } });
      db.projects.put(updated);
      get().showToast(current ? 'Site plan imported' : 'New project created with site plan');
    },

    importCVTemplate: (template) => {
      const now = new Date().toISOString();
      // CV-806 — hydrate omitted *var groups for every templated panel.
      const panels = template.panels.map(p => mergePanelDefaults({
        ...p,
        id:        crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
      }));
      const proj: CVProject = {
        id:        crypto.randomUUID(),
        name:      template.name || 'Template Project',
        buildingName: '',
        jobNumber: '',
        client:    '',
        engineer:  '',
        superintendent: '',
        city:      '',
        state:     '',
        phone:     '',
        fax:       '',
        email:     '',
        panels,
        site:      null,
        plans:     null,
        elevations: defaultElevations(),
        settings:  template.settings || defaultProjectSettings(),
        createdAt: now,
        updatedAt: now,
      };
      set({ project: proj, ui: { ...DEFAULT_UI, editMode: 'project', activePanelId: panels[0]?.id ?? null } });
      db.projects.put(proj).catch(err => {
        if (import.meta.env.DEV) console.error('[CV] importCVTemplate persist error:', err);
      });
      get().showToast('Template imported — fill in job details to get started');
    },

    refreshProjectList: async () => {
      try {
        const list = await db.projects.orderBy('updatedAt').reverse().toArray();
        set({ projectList: list.map(p => ({ id: p.id, name: p.name, updatedAt: p.updatedAt })) });
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] refreshProjectList error:', err);
        // Non-fatal: leave existing list in place
      }
    },

    deleteProject: async (id) => {
      try {
        await db.projects.delete(id);
        await get().refreshProjectList();
        if (get().project?.id === id) set({ project: null, ui: DEFAULT_UI });
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] deleteProject error:', err);
        get().showToast('Delete failed — storage error');
      }
    },

    updateProject: (patch) => {
      const { project } = get();
      if (!project) return;
      const updated = { ...project, ...patch, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    updateSite: (patch) => {
      const { project } = get();
      if (!project) return;
      const site = project.site ? { ...project.site, ...patch } : null;
      const updated = { ...project, site, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    // ── Edit Mode ─────────────────────────────────────────────────────────────
    setEditMode: (mode) => {
      get().createSnapshot();
      set(s => {
        const ui = { ...s.ui, editMode: mode };
        if (mode !== 'panel') ui.selectedEntities = [];
        if (mode !== 'elevations')  ui.selectedPanelIds = [];
        // Sync 2D/3D preference across mode switches
        if (mode === 'panel') ui.viewMode = s.ui.elevationViewMode === '3d' ? '3d' : '2d';
        if (mode === 'elevations')  ui.elevationViewMode = s.ui.viewMode === '2d' ? '2d' : '3d';
        const lastView = computeAppView(ui);
        const project = s.project ? { ...s.project, lastView } : s.project;
        return { ui, project };
      });
      const p = get().project;
      if (p) db.projects.put(p).catch(() => {});
    },

    // ── Elevations ────────────────────────────────────────────────────────────
    addElevation: (name, direction) => {
      const { project } = get();
      if (!project) return;
      const elevations = project.elevations ?? [];
      if (elevations.length >= MAX_ELEVATIONS) {
        get().showToast(`Maximum ${MAX_ELEVATIONS} elevations allowed`);
        return;
      }
      const elev: Elevation = {
        id: crypto.randomUUID(),
        name,
        direction,
        panelIds: [],
        sortOrder: elevations.length,
        ...defaultCorners(direction),
      };
      const updated = { ...project, elevations: [...elevations, elev], updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    removeElevation: (id) => {
      const { project } = get();
      if (!project) return;
      const updated = {
        ...project,
        elevations: (project.elevations ?? []).filter(e => e.id !== id),
        updatedAt: new Date().toISOString(),
      };
      set({ project: updated });
      db.projects.put(updated);
    },

    renameElevation: (id, name) => {
      const { project } = get();
      if (!project) return;
      const elevations = (project.elevations ?? []).map(e =>
        e.id === id ? { ...e, name } : e
      );
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    assignPanelToElevation: (elevationId, panelId) => {
      const { project } = get();
      if (!project) return;
      // Remove from any other elevation first, then add to target
      const elevations = (project.elevations ?? []).map(e => {
        const without = e.panelIds.filter(pid => pid !== panelId);
        if (e.id === elevationId) return { ...e, panelIds: [...without, panelId] };
        return { ...e, panelIds: without };
      });
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    unassignPanelFromElevation: (elevationId, panelId) => {
      const { project } = get();
      if (!project) return;
      const elevations = (project.elevations ?? []).map(e =>
        e.id === elevationId ? { ...e, panelIds: e.panelIds.filter(pid => pid !== panelId) } : e
      );
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    reorderElevation: (fromIndex, toIndex) => {
      const { project } = get();
      if (!project) return;
      const elevations = [...(project.elevations ?? [])];
      const [moved] = elevations.splice(fromIndex, 1);
      if (!moved) return;
      elevations.splice(toIndex, 0, moved);
      // Reassign sortOrder sequentially
      elevations.forEach((e, i) => { e.sortOrder = i; });
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    reorderPanelInElevation: (elevationId, fromIndex, toIndex) => {
      const { project } = get();
      if (!project) return;
      const elevations = (project.elevations ?? []).map(e => {
        if (e.id !== elevationId) return e;
        const ids = [...e.panelIds];
        const [moved] = ids.splice(fromIndex, 1);
        if (!moved) return e;
        ids.splice(toIndex, 0, moved);
        return { ...e, panelIds: ids };
      });
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    splitElevation: (id, sections, panelsPerSection) => {
      const { project } = get();
      if (!project) return;
      const elevations = project.elevations ?? [];
      const idx = elevations.findIndex(e => e.id === id);
      if (idx === -1) return;
      const source = elevations[idx];

      // Validate section count
      if (sections < 2 || sections > 5) {
        get().showToast('Split requires 2–5 sections');
        return;
      }
      if (elevations.length - 1 + sections > MAX_ELEVATIONS) {
        get().showToast(`Cannot split: would exceed ${MAX_ELEVATIONS} elevations`);
        return;
      }

      // Grow panel pool by cloning last panel if needed
      let allPanels = [...project.panels];
      const totalNeeded = panelsPerSection.reduce((s, n) => s + n, 0);
      let panelPool = [...source.panelIds];
      while (panelPool.length < totalNeeded && panelPool.length > 0) {
        const lastId = panelPool[panelPool.length - 1];
        const lastPanel = allPanels.find(p => p.id === lastId);
        if (!lastPanel) break;
        const clone = deepClonePanel(lastPanel, allPanels);
        allPanels = [...allPanels, clone];
        panelPool.push(clone.id);
      }

      // Build section elevations
      const names = splitSectionNames(source.direction, sections);
      let cursor = 0;
      const newElevations: Elevation[] = names.map((name, i) => {
        const count = panelsPerSection[i] ?? 0;
        const pids = panelPool.slice(cursor, cursor + count);
        cursor += count;
        return {
          id: crypto.randomUUID(),
          name,
          direction: source.direction,
          panelIds: pids,
          sortOrder: idx + i,
          leftCorner:  i === 0 ? (source.leftCorner ?? 'continuous') : 'continuous' as CornerType,
          rightCorner: i === sections - 1 ? (source.rightCorner ?? 'continuous') : 'continuous' as CornerType,
          // Connections: outer ends inherit source's external connections, inner ends auto-connect
          leftConnectsTo:  i === 0 ? source.leftConnectsTo : undefined,   // filled below
          rightConnectsTo: i === sections - 1 ? source.rightConnectsTo : undefined,
        };
      });
      // Wire inner adjacent sections to each other (continuous expansion joints)
      for (let i = 0; i < newElevations.length - 1; i++) {
        newElevations[i].rightConnectsTo = newElevations[i + 1].id;
        newElevations[i + 1].leftConnectsTo = newElevations[i].id;
      }

      // Replace source with new sections, reindex sortOrder
      const result = [
        ...elevations.slice(0, idx),
        ...newElevations,
        ...elevations.slice(idx + 1),
      ];
      result.forEach((e, i) => { e.sortOrder = i; });

      // Update external elevations that pointed at the split source
      const firstNew = newElevations[0];
      const lastNew  = newElevations[newElevations.length - 1];
      for (const e of result) {
        if (newElevations.includes(e)) continue;
        if (e.leftConnectsTo === id)  e.leftConnectsTo  = lastNew.id;
        if (e.rightConnectsTo === id) e.rightConnectsTo = firstNew.id;
      }

      const updated = { ...project, panels: allPanels, elevations: result, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
      get().showToast(`Split into ${sections} sections`);
    },

    joinElevations: (ids) => {
      const { project } = get();
      if (!project || ids.length < 2) return;
      const elevations = project.elevations ?? [];
      const targets = ids.map(id => elevations.find(e => e.id === id)).filter((e): e is Elevation => e != null);
      if (targets.length < 2) return;
      const dir = targets[0].direction;
      if (!targets.every(e => e.direction === dir)) {
        get().showToast('Can only join elevations with the same direction');
        return;
      }
      const mergedPanelIds = targets.flatMap(e => e.panelIds);
      const firstIdx = elevations.indexOf(targets[0]);
      const joined: Elevation = {
        id: crypto.randomUUID(),
        name: '',  // placeholder — renamed below
        direction: dir,
        panelIds: mergedPanelIds,
        sortOrder: firstIdx,
        leftCorner:  targets[0].leftCorner ?? defaultCorners(dir).leftCorner,
        rightCorner: targets[targets.length - 1].rightCorner ?? defaultCorners(dir).rightCorner,
        leftConnectsTo:  targets[0].leftConnectsTo,
        rightConnectsTo: targets[targets.length - 1].rightConnectsTo,
      };
      const removeSet = new Set(ids);
      const result = elevations.filter(e => !removeSet.has(e.id));
      result.splice(Math.min(firstIdx, result.length), 0, joined);
      result.forEach((e, i) => { e.sortOrder = i; });

      // Update external elevations that pointed at removed sections
      for (const e of result) {
        if (e.id === joined.id) continue;
        if (e.leftConnectsTo && removeSet.has(e.leftConnectsTo))  e.leftConnectsTo  = joined.id;
        if (e.rightConnectsTo && removeSet.has(e.rightConnectsTo)) e.rightConnectsTo = joined.id;
      }

      // Re-derive valid names for ALL sections of this direction
      const dirSections = result.filter(e => e.direction === dir).sort((a, b) => a.sortOrder - b.sortOrder);
      const dirName = dir === 'B' ? 'Back' : dir === 'F' ? 'Front' : dir === 'R' ? 'Right' : dir === 'L' ? 'Left' : dir;
      if (dirSections.length === 1) {
        dirSections[0].name = `${dirName} Elevation`;
      } else if (dirSections.length >= 2 && dirSections.length <= 5) {
        const names = splitSectionNames(dir, dirSections.length);
        dirSections.forEach((e, i) => { e.name = names[i]; });
      }
      const updated = { ...project, elevations: result, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
      get().showToast(`Joined ${targets.length} elevations`);
    },

    sortElevation: (id, order) => {
      const { project } = get();
      if (!project) return;
      const panels = project.panels;
      const elevations = (project.elevations ?? []).map(e => {
        if (e.id !== id) return e;
        const sorted = [...e.panelIds].sort((a, b) => {
          const ma = panels.find(p => p.id === a)?.mpvar.panelMark ?? '';
          const mb = panels.find(p => p.id === b)?.mpvar.panelMark ?? '';
          return order === 'asc' ? ma.localeCompare(mb, undefined, { numeric: true }) : mb.localeCompare(ma, undefined, { numeric: true });
        });
        return { ...e, panelIds: sorted };
      });
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    setElevationCorner: (id, side, type) => {
      const { project } = get();
      if (!project) return;
      const allElevs = project.elevations ?? [];
      // Find the target elevation to determine its connected neighbor
      const target = allElevs.find(e => e.id === id);
      const mirrorId = target
        ? (side === 'left' ? target.leftConnectsTo : target.rightConnectsTo)
        : undefined;

      const elevations = allElevs.map(e => {
        if (e.id === id) {
          const clone = { ...e };
          if (side === 'left') {
            clone.leftCorner = type;
            if (type !== 'continuous' && !clone.leftConnectsTo) {
              const sorted = [...allElevs].sort((a, b) => a.sortOrder - b.sortOrder);
              const idx = sorted.findIndex(s => s.id === id);
              const prev = idx > 0 ? sorted[idx - 1] : sorted[sorted.length - 1];
              if (prev && prev.id !== id) clone.leftConnectsTo = prev.id;
            }
          } else {
            clone.rightCorner = type;
            if (type !== 'continuous' && !clone.rightConnectsTo) {
              const sorted = [...allElevs].sort((a, b) => a.sortOrder - b.sortOrder);
              const idx = sorted.findIndex(s => s.id === id);
              const next = idx < sorted.length - 1 ? sorted[idx + 1] : sorted[0];
              if (next && next.id !== id) clone.rightConnectsTo = next.id;
            }
          }
          return clone;
        }
        // Mirror: update neighbor's opposite corner to the correct complement
        if (mirrorId && e.id === mirrorId) {
          const mirrorSide = side === 'left' ? 'rightCorner' : 'leftCorner';
          const mirrorType = complementCornerType(type, e[mirrorSide]);
          if (e[mirrorSide] !== mirrorType) return { ...e, [mirrorSide]: mirrorType };
        }
        return e;
      });
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    setElevationConnection: (id, side, targetId) => {
      const { project } = get();
      if (!project) return;
      const elevations = (project.elevations ?? []).map(e => {
        if (e.id !== id) return e;
        return side === 'left'
          ? { ...e, leftConnectsTo: targetId }
          : { ...e, rightConnectsTo: targetId };
      });
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    movePanelBetweenElevations: (fromElevId, panelIdx, toElevId, toIdx) => {
      const { project } = get();
      if (!project || fromElevId === toElevId) return;
      const elevations = (project.elevations ?? []).map(e => ({ ...e, panelIds: [...e.panelIds] }));
      const from = elevations.find(e => e.id === fromElevId);
      const to = elevations.find(e => e.id === toElevId);
      if (!from || !to || panelIdx < 0 || panelIdx >= from.panelIds.length) return;
      const [panelId] = from.panelIds.splice(panelIdx, 1);
      to.panelIds.splice(Math.min(toIdx, to.panelIds.length), 0, panelId);
      const updated = { ...project, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...get().ui, activePanelId: panelId } });
      db.projects.put(updated);
    },

    clonePanelAtPosition: (panelId, elevationId, position) => {
      const { project } = get();
      if (!project) return;
      const srcPanel = project.panels.find(p => p.id === panelId);
      if (!srcPanel) return;

      const clone = deepClonePanel(srcPanel, project.panels);
      const allPanels = [...project.panels, clone];

      // Insert clone ID into the elevation's panelIds at the right position
      const elevations = (project.elevations ?? []).map(e => {
        if (e.id !== elevationId) return e;
        const pids = [...e.panelIds];
        const srcIdx = pids.indexOf(panelId);
        if (srcIdx === -1) return { ...e, panelIds: [...pids, clone.id] };
        const insertAt = position === 'before' ? srcIdx : srcIdx + 1;
        pids.splice(insertAt, 0, clone.id);
        return { ...e, panelIds: pids };
      });

      const updated = { ...project, panels: allPanels, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    // ── Snapshots ──────────────────────────────────────────────────────────────
    createSnapshot: async () => {
      const { project } = get();
      if (!project) return;
      const snap = {
        projectId: project.id,
        createdAt: new Date().toISOString(),
        data: structuredClone(project),
      };
      try {
        await db.snapshots.add(snap);
        // FIFO pruning
        const maxKeep = project.settings?.maxSnapshots ?? 20;
        const all = await db.snapshots
          .where('projectId').equals(project.id)
          .sortBy('createdAt');
        if (all.length > maxKeep) {
          const toDelete = all.slice(0, all.length - maxKeep).map(s => s.autoId!);
          await db.snapshots.bulkDelete(toDelete);
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] createSnapshot error:', err);
      }
    },

    restoreSnapshot: async (autoId) => {
      try {
        const snap = await db.snapshots.get(autoId);
        if (!snap) { get().showToast('Snapshot not found'); return; }
        const restored = { ...snap.data, updatedAt: new Date().toISOString() };
        set({ project: restored, ui: { ...get().ui, activePanelId: restored.panels[0]?.id ?? null } });
        await db.projects.put(restored);
        get().showToast('Snapshot restored');
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] restoreSnapshot error:', err);
        get().showToast('Failed to restore snapshot');
      }
    },

    deleteSnapshot: async (autoId) => {
      try {
        await db.snapshots.delete(autoId);
      } catch (err) {
        if (import.meta.env.DEV) console.error('[CV] deleteSnapshot error:', err);
      }
    },

    getSnapshots: async () => {
      const { project } = get();
      if (!project) return [];
      try {
        return await db.snapshots
          .where('projectId').equals(project.id)
          .reverse()
          .sortBy('createdAt');
      } catch {
        return [];
      }
    },

    // ── Panels ────────────────────────────────────────────────────────────────
    addPanel: () => {
      const { project } = get();
      if (!project) return;
      const panel = defaultPanelData(crypto.randomUUID());
      panel.mpvar.panelMark = nextPanelMark(project.panels);
      if (project.name && !panel.mpvar.projectName) {
        panel.mpvar.projectName = project.name;
      }
      const updated = { ...project, panels: [...project.panels, panel], updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...get().ui, activePanelId: panel.id } });
      db.projects.put(updated);
    },

    deletePanel: (id) => {
      const { project } = get();
      if (!project) return;
      const panels = project.panels.filter(p => p.id !== id);
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      const nextActive = panels[0]?.id ?? null;
      set({ project: updated, ui: { ...get().ui, activePanelId: nextActive } });
      db.projects.put(updated);
    },

    duplicatePanel: (id) => {
      const { project } = get();
      if (!project) return;
      const src = project.panels.find(p => p.id === id);
      if (!src) return;
      const copy = { ...src, id: crypto.randomUUID(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      copy.mpvar = { ...src.mpvar, panelMark: nextPanelMark(project.panels) };
      const updated = { ...project, panels: [...project.panels, copy], updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...get().ui, activePanelId: copy.id } });
      db.projects.put(updated);
    },

    setActivePanel: (id) => set(s => ({ ui: { ...s.ui, activePanelId: id } })),
    setActivePanelId: (id) => set(s => ({ ui: { ...s.ui, activePanelId: id } })),

    updatePanel: (panelId, patch) => {
      const { project } = get();
      if (!project) return;
      const updated = { ...project, panels: patchPanel(project.panels, panelId, patch), updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    updateMpVar: (panelId, patch) => {
      const { project } = get();
      if (!project) return;
      const panels = project.panels.map(p => {
        if (p.id !== panelId) return p;
        const oldW = p.mpvar.width;
        const newW = patch.width;
        let result: PanelData = { ...p, mpvar: { ...p.mpvar, ...patch }, updatedAt: new Date().toISOString() };

        // Centered resize: shift features so they stay the same distance from panel center
        if (newW !== undefined && newW !== oldW) {
          const shift = (newW - oldW) / 2;

          // Helper: shift x in a Slot array
          const shiftSlots = <T extends { x: number }>(slots: Slot<T>[]): Slot<T>[] =>
            slots.map(s => s.enabled ? { ...s, data: { ...s.data, x: s.data.x + shift } } : s);

          result = {
            ...result,
            rovar: shiftSlots(result.rovar),
            wdvar: shiftSlots(result.wdvar),
            drvar: shiftSlots(result.drvar),
            dlvar: shiftSlots(result.dlvar),
            sbvar: shiftSlots(result.sbvar),
            rbvar: shiftSlots(result.rbvar),
            fvvar: shiftSlots(result.fvvar),
            tsvar: shiftSlots(result.tsvar),
            fsvar: shiftSlots(result.fsvar),
            ssvar: shiftSlots(result.ssvar),
            sdvar: shiftSlots(result.sdvar),
            bpvar: shiftSlots(result.bpvar),
            ppvar: shiftSlots(result.ppvar),
            wcvar: result.wcvar.map(s => {
              if (!s.enabled) return s;
              // Edge-pinned welds stay at edges; custom-position welds shift
              if (s.data.sideL || s.data.sideR) return s;
              return { ...s, data: { ...s.data, x: s.data.x + shift } };
            }),
            // Horizontal strips: edge-pinned ends track the edge, others shift
            fhvar: result.fhvar.map(s => {
              if (!s.enabled) return s;
              const d = s.data;
              const xs = d.xStart === 0 ? 0 : d.xStart + shift;
              const xe = d.xEnd === oldW ? newW : d.xEnd + shift;
              return { ...s, data: { ...d, xStart: xs, xEnd: xe, width: xe - xs } };
            }),
          };
        }

        return result;
      });
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    updateChVar: (panelId, patch) => {
      const { project } = get();
      if (!project) return;
      const panels = project.panels.map(p =>
        p.id === panelId ? { ...p, chvar: { ...p.chvar, ...patch }, updatedAt: new Date().toISOString() } : p
      );
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    updateTpVar: (panelId, patch) => {
      const { project } = get();
      if (!project) return;
      const panels = project.panels.map(p =>
        p.id === panelId ? { ...p, tpvar: { ...p.tpvar, ...patch }, updatedAt: new Date().toISOString() } : p
      );
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    updateLbVar: (panelId, patch) => {
      const { project } = get();
      if (!project) return;
      const panels = project.panels.map(p =>
        p.id === panelId ? { ...p, lbvar: { ...p.lbvar, ...patch }, updatedAt: new Date().toISOString() } : p
      );
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      set({ project: updated });
      db.projects.put(updated);
    },

    // ── Selection ─────────────────────────────────────────────────────────────
    selectEntity: (id) => set(s => ({ ui: { ...s.ui, selectedEntities: [id] } })),
    toggleSelectEntity: (id) => set(s => {
      const sel = s.ui.selectedEntities;
      const next = sel.includes(id) ? sel.filter(e => e !== id) : [...sel, id];
      return { ui: { ...s.ui, selectedEntities: next } };
    }),
    setSelection: (ids) => set(s => ({ ui: { ...s.ui, selectedEntities: ids } })),
    addToSelection: (ids) => set(s => {
      const existing = new Set(s.ui.selectedEntities);
      for (const id of ids) existing.add(id);
      return { ui: { ...s.ui, selectedEntities: [...existing] } };
    }),
    clearSelection: () => set(s => {
      if (s.ui.selectedEntities.length === 0) return s; // no-op
      return { ui: { ...s.ui, selectedEntities: [] } };
    }),
    deleteSelectedEntities: () => {
      const { project, ui } = get();
      if (!project || !ui.activePanelId || ui.selectedEntities.length === 0) return;
      const panelId = ui.activePanelId;
      const sel = new Set(ui.selectedEntities);
      const panel = project.panels.find(p => p.id === panelId);
      if (!panel) return;

      // Slot-based groups: disable matching slots
      const SLOT_GROUPS = ['rovar','wdvar','drvar','dlvar','sbvar','rbvar','fhvar','fvvar','bpvar','ppvar','wcvar'] as const;
      type SlotKey = typeof SLOT_GROUPS[number];
      const patch: Partial<Record<SlotKey, typeof panel[SlotKey]>> = {};
      for (const grp of SLOT_GROUPS) {
        const arr = panel[grp] as { enabled: boolean }[];
        let changed = false;
        const updated = arr.map((slot, i) => {
          if (sel.has(`${grp}-${i}`) && slot.enabled) {
            changed = true;
            return { ...slot, enabled: false };
          }
          return slot;
        });
        if (changed) (patch as Record<string, unknown>)[grp] = updated;
      }
      if (Object.keys(patch).length > 0) {
        const panels = patchPanel(project.panels, panelId, patch as Partial<PanelData>);
        const updated = { ...project, panels, updatedAt: new Date().toISOString() };
        set({ project: updated, ui: { ...ui, selectedEntities: [] } });
        db.projects.put(updated);
      } else {
        set(s => ({ ui: { ...s.ui, selectedEntities: [] } }));
      }
    },

    // ── Panel Selection ──────────────────────────────────────────────────────
    selectPanel: (id) => set(s => ({
      ui: { ...s.ui, selectedPanelIds: [id], selectedEntities: [] },
    })),
    toggleSelectPanel: (id) => set(s => {
      const sel = s.ui.selectedPanelIds;
      const next = sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id];
      return { ui: { ...s.ui, selectedPanelIds: next } };
    }),
    setSelectedPanels: (ids) => set(s => ({
      ui: { ...s.ui, selectedPanelIds: ids, selectedEntities: [] },
    })),
    clearPanelSelection: () => set(s => {
      if (s.ui.selectedPanelIds.length === 0) return s;
      return { ui: { ...s.ui, selectedPanelIds: [] } };
    }),
    copySelectedPanels: () => {
      const { project, ui } = get();
      if (!project || ui.selectedPanelIds.length === 0) return;
      const first = project.panels.find(p => p.id === ui.selectedPanelIds[0]);
      if (!first) return;
      set(s => ({ ui: { ...s.ui, panelClipboard: structuredClone(first) } }));
    },
    pastePanelsToElevation: (elevationId, afterPanelId?) => {
      const { project, ui } = get();
      if (!project || !ui.panelClipboard) return;
      const clone = deepClonePanel(ui.panelClipboard, project.panels);
      const allPanels = [...project.panels, clone];
      const elevations = (project.elevations ?? []).map(e => {
        if (e.id !== elevationId) return e;
        const pids = [...e.panelIds];
        if (afterPanelId) {
          const idx = pids.indexOf(afterPanelId);
          if (idx !== -1) { pids.splice(idx + 1, 0, clone.id); return { ...e, panelIds: pids }; }
        }
        return { ...e, panelIds: [...pids, clone.id] };
      });
      const updated = { ...project, panels: allPanels, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...ui, selectedPanelIds: [clone.id] } });
      db.projects.put(updated);
    },
    deleteSelectedPanels: () => {
      const { project, ui } = get();
      if (!project || ui.selectedPanelIds.length === 0) return;
      const selSet = new Set(ui.selectedPanelIds);
      const panels = project.panels.filter(p => !selSet.has(p.id));
      const elevations = (project.elevations ?? []).map(e => ({
        ...e,
        panelIds: e.panelIds.filter(pid => !selSet.has(pid)),
      }));
      const nextActive = panels[0]?.id ?? null;
      const updated = { ...project, panels, elevations, updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...ui, activePanelId: nextActive, selectedPanelIds: [] } });
      db.projects.put(updated);
    },

    // ── UI ────────────────────────────────────────────────────────────────────
    openDialog:       (d) => set(s => ({ ui: { ...s.ui, activeDialog: d } })),
    setActiveDialog:  (d) => set(s => ({ ui: { ...s.ui, activeDialog: d } })),
    closeDialog: () => set(s => {
      const panel    = s.project?.panels.find(p => p.id === s.ui.activePanelId);
      const warnings = panel ? getStructuralWarnings(panel, s.project!.settings) : [];
      return { ui: { ...s.ui, activeDialog: 'none', pendingWarnings: warnings.length > 0 ? warnings : null } };
    }),
    clearPendingWarnings: () => set(s => ({ ui: { ...s.ui, pendingWarnings: null } })),
    showWarnings: (panelId) => set(s => {
      const panel = s.project?.panels.find(p => p.id === panelId);
      const warnings = panel ? getStructuralWarnings(panel, s.project!.settings) : [];
      return { ui: { ...s.ui, pendingWarnings: warnings.length > 0 ? warnings : null } };
    }),
    setFocusField: (field) => set(s => ({ ui: { ...s.ui, focusField: field } })),

    // ── Placement & Context Menu ──────────────────────────────────────────────
    startPlacement: (panelX, panelY, screenX, screenY) =>
      set(s => ({ ui: { ...s.ui, placementContext: { panelX, panelY, screenX, screenY }, contextMenu: null } })),

    commitPlacement: (group) => {
      const { project, ui } = get();
      if (!project || !ui.activePanelId || !ui.placementContext) return;
      const panel = project.panels.find(p => p.id === ui.activePanelId);
      if (!panel) return;
      const raw = panel[group as keyof PanelData];
      if (!Array.isArray(raw)) return;
      const slots = raw as unknown as Slot<Record<string, unknown>>[];
      const idx = findNextSlot(slots);
      if (idx < 0) {
        get().showToast(`No available slots for ${groupPrefix(group)}`);
        set(s => ({ ui: { ...s.ui, placementContext: null } }));
        return;
      }
      const dialog = groupToDialog(group);
      if (!dialog) return;
      const { panelX, panelY } = ui.placementContext;
      set(s => ({
        ui: {
          ...s.ui,
          placementContext: null,
          pendingSlotTarget: { group, index: idx, panelX, panelY },
          activeDialog: dialog,
        },
      }));
    },

    cancelPlacement: () => set(s => ({ ui: { ...s.ui, placementContext: null } })),

    showContextMenu: (entityId, screenX, screenY, panelX, panelY) =>
      set(s => ({ ui: { ...s.ui, contextMenu: { entityId, screenX, screenY, panelX, panelY }, placementContext: null } })),

    closeContextMenu: () => set(s => ({ ui: { ...s.ui, contextMenu: null } })),

    copyEntity: (entityId) => {
      const { project, ui } = get();
      if (!project || !ui.activePanelId) return;
      const panel = project.panels.find(p => p.id === ui.activePanelId);
      if (!panel) return;
      const [grp, idxStr] = entityId.split('-');
      const idx = parseInt(idxStr, 10);
      const raw = panel[grp as keyof PanelData];
      if (!Array.isArray(raw)) return;
      const slots = raw as unknown as Slot<Record<string, unknown>>[];
      if (!slots[idx]?.enabled) return;
      set(s => ({ ui: { ...s.ui, clipboard: { group: grp, data: { ...slots[idx].data } }, contextMenu: null } }));
      get().showToast(`Copied ${grp.toUpperCase().replace('VAR', '')}-${idx}`);
    },

    pasteEntity: (panelX, panelY) => {
      const { project, ui } = get();
      if (!project || !ui.activePanelId || !ui.clipboard) return;
      const panel = project.panels.find(p => p.id === ui.activePanelId);
      if (!panel) return;
      const { group, data } = ui.clipboard;
      const raw = panel[group as keyof PanelData];
      if (!Array.isArray(raw)) return;
      const slots = raw as unknown as Slot<Record<string, unknown>>[];
      const idx = findNextSlot(slots);
      if (idx < 0) {
        get().showToast(`No available slots for ${groupPrefix(group)}`);
        set(s => ({ ui: { ...s.ui, contextMenu: null } }));
        return;
      }
      const mark = autoMark(groupPrefix(group), slots as Slot<{ mark: string }>[]);
      const newData = { ...data, mark };
      // Set position if the data has x/y fields
      if ('x' in newData) (newData as Record<string, unknown>).x = panelX;
      if ('y' in newData) (newData as Record<string, unknown>).y = panelY;
      const newSlots = [...slots];
      newSlots[idx] = { enabled: true, data: newData };
      const patch = { [group]: newSlots } as Partial<PanelData>;
      const panels = patchPanel(project.panels, ui.activePanelId, patch);
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...ui, contextMenu: null } });
      db.projects.put(updated);
      get().showToast(`Pasted ${mark}`);
    },

    editEntity: (entityId) => {
      const [grp] = entityId.split('-');
      const dialog = groupToDialog(grp);
      if (!dialog) return;
      const idx = parseInt(entityId.split('-')[1], 10);
      set(s => ({
        ui: {
          ...s.ui,
          pendingSlotTarget: { group: grp, index: idx, panelX: 0, panelY: 0 },
          activeDialog: dialog,
          contextMenu: null,
        },
      }));
    },

    deleteEntity: (entityId) => {
      const { project, ui } = get();
      if (!project || !ui.activePanelId) return;
      const panel = project.panels.find(p => p.id === ui.activePanelId);
      if (!panel) return;
      const [grp, idxStr] = entityId.split('-');
      const idx = parseInt(idxStr, 10);
      const raw = panel[grp as keyof PanelData];
      if (!Array.isArray(raw)) return;
      const slots = raw as unknown as Slot<Record<string, unknown>>[];
      if (!slots[idx]?.enabled) return;
      const newSlots = slots.map((s, i) => i === idx ? { ...s, enabled: false } : s);
      const patch = { [grp]: newSlots } as Partial<PanelData>;
      const panels = patchPanel(project.panels, ui.activePanelId, patch);
      const updated = { ...project, panels, updatedAt: new Date().toISOString() };
      set({ project: updated, ui: { ...ui, contextMenu: null, selectedEntities: [] } });
      db.projects.put(updated);
    },

    clearPendingSlotTarget: () => set(s => ({ ui: { ...s.ui, pendingSlotTarget: null } })),
    setFaceFilter: (filter) => set(s => ({ ui: { ...s.ui, faceFilter: filter } })),
    toggleLayerVisibility: (mt) => set(s => ({
      ui: { ...s.ui, layerVisibility: { ...s.ui.layerVisibility, [mt]: !s.ui.layerVisibility[mt] } }
    })),
    setLayerVisibility: (mt, visible) => set(s => ({
      ui: { ...s.ui, layerVisibility: { ...s.ui.layerVisibility, [mt]: visible } }
    })),
    toggleCheckedPanel: (id) => set(s => {
      const ids = s.ui.checkedPanelIds;
      const next = ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id];
      return { ui: { ...s.ui, checkedPanelIds: next } };
    }),
    checkAllPanels: () => set(s => {
      const ids = s.project?.panels.map(p => p.id) ?? [];
      return { ui: { ...s.ui, checkedPanelIds: ids } };
    }),
    uncheckAllPanels: () => set(s => ({ ui: { ...s.ui, checkedPanelIds: [] } })),

    // ── Multi-floor actions ─────────────────────────────────────────────────
    setActiveFloorId: (id) => set(s => ({ ui: { ...s.ui, activeFloorId: id } })),

    cloneFloor: (sourceFloorId) => set(s => {
      const proj = s.project;
      if (!proj) return s;
      // Initialize floors[] lazily if missing — first floor wraps all existing elevations.
      const existing: Floor[] = proj.floors ?? [{
        id: crypto.randomUUID(),
        name: 'Floor 1',
        type: 'standard',
        elevationIds: proj.elevations.map(e => e.id),
        slabThickness: 5,
      }];
      const source = existing.find(f => f.id === sourceFloorId) ?? existing[0];
      if (!source) return s;
      // Clone source floor metadata; create new (empty-of-panels) elevation copies so each floor has its own panels.
      const newElevs: Elevation[] = [];
      for (const eid of source.elevationIds) {
        const src = proj.elevations.find(e => e.id === eid);
        if (!src) continue;
        newElevs.push({ ...src, id: crypto.randomUUID(), panelIds: [] });
      }
      const nextFloor: Floor = {
        id: crypto.randomUUID(),
        name: `Floor ${existing.length + 1}`,
        type: 'standard',
        elevationIds: newElevs.map(e => e.id),
        slabThickness: source.slabThickness ?? 5,
      };
      const nextProject: CVProject = {
        ...proj,
        elevations: [...proj.elevations, ...newElevs],
        floors: [...existing, nextFloor],
        updatedAt: new Date().toISOString(),
      };
      db.projects.put(nextProject).catch(() => {});
      return { project: nextProject, ui: { ...s.ui, activeFloorId: nextFloor.id } };
    }),

    removeFloor: (id) => set(s => {
      const proj = s.project;
      if (!proj || !proj.floors || proj.floors.length <= 1) return s;
      const target = proj.floors.find(f => f.id === id);
      if (!target) return s;
      const removeElev = new Set(target.elevationIds);
      const removePanel = new Set(
        proj.elevations.filter(e => removeElev.has(e.id)).flatMap(e => e.panelIds),
      );
      const nextProject: CVProject = {
        ...proj,
        floors: proj.floors.filter(f => f.id !== id),
        elevations: proj.elevations.filter(e => !removeElev.has(e.id)),
        panels: proj.panels.filter(p => !removePanel.has(p.id)),
        updatedAt: new Date().toISOString(),
      };
      const nextActive = s.ui.activeFloorId === id ? (nextProject.floors?.[0]?.id ?? null) : s.ui.activeFloorId;
      db.projects.put(nextProject).catch(() => {});
      return { project: nextProject, ui: { ...s.ui, activeFloorId: nextActive } };
    }),

    // ── Brace plan actions ─────────────────────────────────────────────────
    setBracingViewMode: (v) => set(s => ({ ui: { ...s.ui, bracingViewMode: v } })),
    setBracePlanSettings: (patch) => set(s => ({
      ui: { ...s.ui, bracePlanSettings: { ...s.ui.bracePlanSettings, ...patch } },
    })),
    toggleBracePanel: (id) => set(s => {
      const has = s.ui.checkedBracePanelIds.includes(id);
      const next = has ? s.ui.checkedBracePanelIds.filter(x => x !== id) : [...s.ui.checkedBracePanelIds, id];
      return { ui: { ...s.ui, checkedBracePanelIds: next } };
    }),
    setBraceAllPanels: (ids, _checked) => set(s => ({ ui: { ...s.ui, checkedBracePanelIds: [...ids] } })),
    setBracePanelOverride: (id, patch) => set(s => ({
      ui: { ...s.ui, bracePanelOverrides: { ...s.ui.bracePanelOverrides, [id]: { ...(s.ui.bracePanelOverrides[id] ?? {}), ...patch } } },
    })),
    clearBracePanelOverride: (id) => set(s => {
      const next = { ...s.ui.bracePanelOverrides };
      delete next[id];
      return { ui: { ...s.ui, bracePanelOverrides: next } };
    }),

    // ── Cast layout actions ────────────────────────────────────────────────
    setCastViewMode: (v) => set(s => ({ ui: { ...s.ui, castViewMode: v } })),
    setCastLayoutSettings: (patch) => set(s => ({
      ui: { ...s.ui, castLayoutSettings: { ...s.ui.castLayoutSettings, ...patch } },
    })),

    setViewMode: (m, v) => {
      set(s => {
        const ui = { ...s.ui, [m]: v };
        const lastView = computeAppView(ui);
        const project = s.project ? { ...s.project, lastView } : s.project;
        return { ui, project };
      });
      const p = get().project;
      if (p) db.projects.put(p).catch(() => {});
    },
    setElevationViewMode: (v) => {
      if (v === get().ui.elevationViewMode) return;
      get().createSnapshot();
      set(s => {
        const ui = { ...s.ui, elevationViewMode: v };
        const lastView = computeAppView(ui);
        const project = s.project ? { ...s.project, lastView } : s.project;
        return { ui, project };
      });
      const p = get().project;
      if (p) db.projects.put(p).catch(() => {});
    },
    setBuildingViewMode: (v) => {
      if (v === get().ui.buildingViewMode) return;
      set(s => ({ ui: { ...s.ui, buildingViewMode: v } }));
    },
    setShadingMode: (v) => set(s => ({ ui: { ...s.ui, shadingMode: v } })),
    setViewPreset:  (v) => set(s => ({ ui: { ...s.ui, viewPreset: v } })),
    clearViewPreset: () => set(s => s.ui.viewPreset === null ? s : { ...s, ui: { ...s.ui, viewPreset: null } }),
    toggleSidebar:  ()  => set(s => ({ ui: { ...s.ui, sidebarOpen: !s.ui.sidebarOpen } })),
    toggleProps:    ()  => set(s => ({ ui: { ...s.ui, propsOpen: !s.ui.propsOpen } })),
    toggleUnits:    ()  => set(s => ({ ui: { ...s.ui, units: s.ui.units === 'imperial' ? 'metric' : 'imperial' } })),
    toggleGrid:     ()  => set(s => ({ ui: { ...s.ui, showGrid: !s.ui.showGrid } })),
    toggleGround:   ()  => set(s => ({ ui: { ...s.ui, showGround: !s.ui.showGround } })),
    togglePanelMarks:() => set(s => ({ ui: { ...s.ui, showPanelMarks: !s.ui.showPanelMarks } })),

    showToast: (msg, durationMs = 2500) => {
      if (toastTimer) clearTimeout(toastTimer);
      set(s => ({ ui: { ...s.ui, toastMessage: msg } }));
      toastTimer = setTimeout(() => set(s => ({ ui: { ...s.ui, toastMessage: null } })), durationMs);
    },
  }))
);

// Convenience hook: shallow-compares object selectors to prevent infinite re-render loops.
// Use instead of useStore() whenever the selector returns a plain object ( { a, b, c } ).
export function useShallowStore<T>(selector: (s: CVStore) => T): T {
  return useStore(useShallow(selector));
}
